import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { preferenceApi } from '@/domains/system-management/apis/preference-management';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import { useAppStore } from '@/store/appStore';
import { useAuthStore } from '@/store/authStore';
import type { PreferenceFileResponse, UpdatePreferenceRequest } from '../types/preference';

export const usePreferenceManagement = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Get preference. Needs a session (backend @login_required); visitors on the
  // public store pages do not fetch it.
  const usePreference = () => {
    const isLogin = useAuthStore((state) => state.isLogin);
    return useQuery({
      queryKey: ['preference'],
      queryFn: () => preferenceApi.getPreference(),
      enabled: isLogin,
    });
  };

  // Update preference
  const useUpdatePreference = (onUpdateSuccess?: () => void) => {
    return useMutation({
      mutationFn: (data: UpdatePreferenceRequest) => preferenceApi.updatePreference(data),
      onSuccess: () => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['preference'] });
            useAppStore.getState().setPreferenceUpdatedAt(Date.now());
            if (onUpdateSuccess) onUpdateSuccess();
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Upload preference file
  const usePreferenceFile = (onApplySuccess?: (response: PreferenceFileResponse) => void) => {
    return useMutation({
      mutationFn: (data: FormData) => preferenceApi.uploadPreferenceFile(data),
      onSuccess: (response) => {
        if (onApplySuccess) onApplySuccess(response);
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };
  return {
    usePreference,
    useUpdatePreference,
    usePreferenceFile,
  };
};
